import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, Loader2 } from "lucide-react";
import { submitContactLead } from "@/lib/api";

const SERVICES = [
  "Website Design",
  "SEO Optimization",
  "Landing Page",
  "Branding",
  "Content Strategy",
  "Not sure yet",
];
const BUDGETS = ["< $5k", "$5k – $10k", "$10k – $25k", "$25k+"];

export default function ContactCTA() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setLoading(true);
    try {
      await submitContactLead(form);
      toast.success("Message sent. We'll reply within 1 business day.");
      setForm({ name: "", email: "", company: "", service: "", budget: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-pad relative">
      <div className="container-apex">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[32px] overflow-hidden border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 md:p-14"
        >
          {/* Glow background */}
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-20%] left-[-10%] w-[520px] h-[520px] rounded-full bg-apex-blue/35 blur-[140px]" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[520px] h-[520px] rounded-full bg-apex-orange/30 blur-[140px]" />
            <div className="absolute top-[10%] right-[30%] w-[380px] h-[380px] rounded-full bg-apex-purple/25 blur-[120px]" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4"><span className="w-6 h-px bg-apex-cyan" /> APEX Innovation Solutions · Let's talk</div>
              <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
                Ready to build a website that <span className="text-apex-orange">actually grows your business?</span>
              </h2>
              <p className="mt-5 text-white/75 text-lg leading-relaxed max-w-md">
                Tell us a bit about your project. <strong className="text-white">APEX Innovation Solutions</strong> replies within one business day with next steps and a strategy call proposal.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 max-w-full">
                <a href="mailto:info@apexinnovationsolutions.com" data-testid="contact-email-link" className="btn-ghost whitespace-nowrap max-w-full">
                  <Mail className="w-4 h-4 shrink-0" /> <span className="truncate">info@apexinnovationsolutions.com</span>
                </a>
                <a href="mailto:News@apexinnovationsolutions.com" data-testid="contact-email-news-link" className="btn-ghost whitespace-nowrap max-w-full">
                  <Mail className="w-4 h-4 shrink-0" /> <span className="truncate">News@apexinnovationsolutions.com</span>
                </a>
                <a href="tel:+917719779448" data-testid="contact-phone-link" className="btn-ghost whitespace-nowrap">
                  <Phone className="w-4 h-4 shrink-0" /> +91 77197 79448
                </a>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs text-white/70 leading-relaxed" data-testid="contact-offices">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="contact-office-in-1">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">
                    <span aria-hidden>🇮🇳</span> India Office · 1
                  </div>
                  <div className="text-white/90 font-semibold text-[11px]">APEX Innovation Solutions</div>
                  <address className="not-italic mt-1 text-[11px]">
                    2nd Floor, 113, Shri Kuber Complex,<br />
                    Near Indo Canadian Bus Services,<br />
                    Jalandhar, Punjab 144001, India
                  </address>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="contact-office-in-2">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">
                    <span aria-hidden>🇮🇳</span> India Office · 2
                  </div>
                  <div className="text-white/90 font-semibold text-[11px]">APEX Innovation Solutions</div>
                  <address className="not-italic mt-1 text-[11px]">
                    H No. 71/G, Gate No. 12, Jyoti Nagar,<br />
                    Jalandhar–1, PO: Jalandhar City,<br />
                    District: Jalandhar, Punjab – 144001
                  </address>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="contact-office-us">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">
                    <span aria-hidden>🇺🇸</span> United States Office
                  </div>
                  <div className="text-white/90 font-semibold text-[11px]">APEX Innovation Solutions LLC</div>
                  <address className="not-italic mt-1 text-[11px]">
                    7533 S Center View Ct, Ste 210,<br />
                    West Jordan, UT 84084, USA
                  </address>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} data-testid="contact-form" className="lg:col-span-7 glass p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Your name" required>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    data-testid="contact-name-input"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                    data-testid="contact-email-input"
                    className={inputCls}
                  />
                </Field>
                <Field label="Company">
                  <input
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    placeholder="Acme Inc."
                    data-testid="contact-company-input"
                    className={inputCls}
                  />
                </Field>
                <Field label="Budget">
                  <select
                    value={form.budget}
                    onChange={update("budget")}
                    data-testid="contact-budget-select"
                    className={inputCls}
                  >
                    <option value="">Select a range</option>
                    {BUDGETS.map((b) => (
                      <option key={b} value={b} className="bg-apex-navy">{b}</option>
                    ))}
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Service you're interested in">
                    <div className="flex flex-wrap gap-2 mt-1">
                      {SERVICES.map((s) => (
                        <button
                          type="button"
                          key={s}
                          onClick={() => setForm((f) => ({ ...f, service: s }))}
                          data-testid={`service-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                            form.service === s
                              ? "bg-apex-orange/15 border-apex-orange/60 text-apex-orange"
                              : "bg-white/[0.04] border-white/10 text-white/80 hover:border-white/30"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="Tell us about your project" required>
                    <textarea
                      required
                      rows={4}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Goals, timeline, current website, anything relevant..."
                      data-testid="contact-message-textarea"
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="btn-primary mt-6 w-full md:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Book a Free Strategy Call
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.2} />
                  </>
                )}
              </button>
              <p className="mt-3 text-xs text-white/50">We reply within 1 business day. No spam, ever.</p>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full mt-1.5 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-apex-cyan/60 focus:bg-white/[0.06] transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-white/70 flex items-center gap-1.5">
        {label} {required && <span className="text-apex-orange">*</span>}
      </span>
      {children}
    </label>
  );
}
